import React from "react";
import cx from "classnames";
import styles from "./styles.module.scss";

interface PaginationIndicatorProps {
  total?: number;
  current?: number;
  totalItems?: number;
  activeItemIndex?: number;
  groupSize?: number;
  maxVisibleIndicators?: number;
  onPageChange?: (itemIndex: number) => void;
}

interface IndicatorProps {
  activeIndicator: boolean;
  faded?: boolean;
  onClick?: () => void;
}

function Indicator({ activeIndicator, faded = false, onClick }: IndicatorProps) {
  return (
    <div
      className={cx(styles.indicator, {
        [styles.activeIndicator]: activeIndicator,
        [styles.faded]: faded,
      })}
      onClick={onClick}
    />
  );
}

const DEFAULT_MAX_VISIBLE = 4;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function PaginationIndicator({
  total = 0,
  current = 0,
  totalItems,
  activeItemIndex = 0,
  groupSize = 1,
  maxVisibleIndicators = DEFAULT_MAX_VISIBLE,
  onPageChange,
}: PaginationIndicatorProps) {
  const maxGroupSize = Math.max(groupSize, 1);
  const totalIndicators = !totalItems 
    ? total
    : Math.ceil(totalItems / maxGroupSize);

  if (totalIndicators <= 1) return null;

  const activeIndicator = !totalItems
    ? clamp(current, 0, totalIndicators - 1) : clamp(Math.floor(activeItemIndex / maxGroupSize), 0, totalIndicators - 1);

  let windowStart = 0;
  if (totalIndicators <= maxVisibleIndicators) {
    windowStart = 0;
  } else {
    windowStart = activeIndicator - Math.floor(maxVisibleIndicators / 2);
    windowStart = clamp(windowStart, 0, totalIndicators - maxVisibleIndicators);
  }

  const windowEnd = Math.min(windowStart + maxVisibleIndicators, totalIndicators);
  const visibleIndices = Array.from({ length: windowEnd - windowStart }, (_, i) => windowStart + i);

  const showLeftFade = windowStart > 0;
  const showRightFade = windowEnd < totalIndicators;

  const handleDotClick = (indicatorIndex: number) => {
    if (!onPageChange) return;
    const targetItemIndex = indicatorIndex * maxGroupSize;
    onPageChange(targetItemIndex);
  };

  return (
    <div className={styles.pagination}>
      {showLeftFade && (
        <Indicator
          activeIndicator={false}
          faded
          onClick={() => handleDotClick(windowStart - 1)}
        />
      )}

      <div className={styles.window}>
        {visibleIndices.map((index) => (
          <Indicator
            key={index}
            activeIndicator={index === activeIndicator}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>

      {showRightFade && (
        <Indicator
          activeIndicator={false}
          faded
          onClick={() => handleDotClick(windowEnd)}
        />
      )}
    </div>
  );
}

export default PaginationIndicator;